import styled from '@emotion/styled';
import { Region } from '@kaizenplatform/hugot-entity';
import { Button, Grid, Typography } from '@material-ui/core';
import { ClassAttributes, createElement, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  CreativeFormatAspectRatio,
  CreativeFormatCreativeType,
  CreativeFormatNetwork,
  CreativeFormatPlacement,
  CreativeFormatPlaybackTime,
} from '../../../graphql/generated/graphql';
import { toOfferCreativeFormatCreativeTypeKey, toOfferCreativeFormatNetworkKey } from '../../../utility/graphql/enum';
import { SPACING_LARGE, SPACING_UNIT } from '../../constant/spacing';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import CreativeFormatCreativeTypeLabel from './CreativeFormatCreativeTypeLabel';
import CreativeFormatNetworkIcon from './CreativeFormatNetworkIcon';
import CreativeFormatPlacementLabel from './CreativeFormatPlacementLabel';

const keysOfProjectTypeShowCriteriaLink = [
  'project_type_jp_for_ad',
  'project_type_jp_for_google_smb',
  'project_type_jp_for_consulting',
];

interface Props {
  creativeFormat: {
    network: Pick<CreativeFormatNetwork, 'key' | 'name'>;
    placement: Pick<CreativeFormatPlacement, 'key' | 'name'>;
    creativeType: Pick<CreativeFormatCreativeType, 'key'>;
    attribute: {
      aspectRatio?: Pick<CreativeFormatAspectRatio, 'aspectRatio' | 'size'> | null;
      playbackTime?: Pick<CreativeFormatPlaybackTime, 'seconds'> | null;
    };
  };
  regionKey?: Region;
  projectTypeKey?: string;
  className?: string;
}

const CreativeFormatDetail = ({
  creativeFormat,
  regionKey,
  projectTypeKey,
  className,
}: ClassAttributes<HTMLElement> & Props) => {
  const isJP = regionKey === 'jp';
  const showReviewCriteriaLink =
    isJP && projectTypeKey ? keysOfProjectTypeShowCriteriaLink.includes(projectTypeKey) : false;
  return (
    <div className={className}>
      <Grid container justify="space-between">
        <Grid item>
          <Title variant="h6">
            <FormattedMessage id="creative_format_detail.title" />
          </Title>
        </Grid>
        <Grid item>
          {showReviewCriteriaLink && (
            <HelpLink href="https://help.kaizen-ad.com/ja/articles/4252114" target="_blank" rel="noopener noreferrer">
              <Button variant="contained" size="small" color="primary">
                <FormattedMessage id="creative_format_detail.review_criteria_items" />
              </Button>
            </HelpLink>
          )}
        </Grid>
      </Grid>

      <Field>
        <Label>
          <FormattedMessage id="creative_format_detail.label.network" />
        </Label>
        <Value>
          <CreativeFormatNetworkIcon network={toOfferCreativeFormatNetworkKey(creativeFormat.network.key)} />
          <NetworkName>{creativeFormat.network.name}</NetworkName>
        </Value>
      </Field>

      <Field>
        <Label>
          <FormattedMessage id="creative_format_detail.label.placement" />
        </Label>
        <Value>
          <Typography>
            <CreativeFormatPlacementLabel placement={creativeFormat.placement} />
          </Typography>
        </Value>
      </Field>

      <Field>
        <Label>
          <FormattedMessage id="creative_format_detail.label.creative_type" />
        </Label>
        <Value>
          <CreativeFormatCreativeTypeLabel
            creativeType={toOfferCreativeFormatCreativeTypeKey(creativeFormat.creativeType.key)}
          />
        </Value>
      </Field>

      {creativeFormat.attribute.aspectRatio &&
        creativeFormat.attribute.aspectRatio.aspectRatio.width *
          creativeFormat.attribute.aspectRatio.aspectRatio.height >
          0 && (
          <Fragment>
            <Field>
              <Label>
                <FormattedMessage id="creative_format_detail.label.aspect_ratio" />
              </Label>
              <Value>
                <Typography>
                  {creativeFormat.attribute.aspectRatio.aspectRatio.width}:
                  {creativeFormat.attribute.aspectRatio.aspectRatio.height}
                </Typography>
              </Value>
            </Field>

            <Field>
              <Label>
                <FormattedMessage id="creative_format_detail.label.display_size" />
              </Label>
              <Value>
                <Typography>
                  <FormattedMessage id="creative_format_detail.sub_label.display_width" />:
                  {`${creativeFormat.attribute.aspectRatio.size.width}, `}
                  <FormattedMessage id="creative_format_detail.sub_label.display_height" />:
                  {creativeFormat.attribute.aspectRatio.size.height}
                </Typography>
              </Value>
            </Field>
          </Fragment>
        )}

      {creativeFormat.attribute.playbackTime && (
        <Field>
          <Label>
            <FormattedMessage id="creative_format_detail.label.seconds" />
          </Label>
          <Value>
            <Typography>
              <FormattedMessage
                id="creative_format.playback_time"
                values={{ seconds: creativeFormat.attribute.playbackTime.seconds }}
              />
            </Typography>
          </Value>
        </Field>
      )}
    </div>
  );
};

const Title = styled(Typography)({
  marginBottom: SPACING_UNIT * 3,
});

const Field = styled('div')(
  gridLayoutContainer({
    columns: ['20%', `${SPACING_UNIT * 3}px`, `calc(100% - 20% - ${SPACING_UNIT * 3}px)`],
    rows: ['1fr'],
    areas: [['label', '.', 'input']],
    alignItems: 'center',
  }),
  {
    marginBottom: SPACING_LARGE,
  },
);

const Label = styled(Typography)(gridLayoutItem('label', { justifySelf: 'flex-end' }), {
  textAlign: 'right',
});

const Value = styled('div')(
  gridLayoutItem('input'),
  flexibleLayoutContainer({ direction: 'row', alignItems: 'center' }),
);

const NetworkName = styled(Typography)({
  marginLeft: SPACING_UNIT,
  lineHeight: 1,
});

const HelpLink = styled('a')({
  textDecoration: 'none',
});

export default CreativeFormatDetail;
